import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, AlertCircle, PieChart } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';

import { FUNDING_BREAKDOWN_CATEGORIES, getCurrencySymbol } from '../../../constants/index.js';

const FundingBreakdownStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [breakdown, setBreakdown] = useState(formData.funding_use || []);
  const [totalPercentage, setTotalPercentage] = useState(0);

  // Calculate total percentage whenever breakdown changes
  useEffect(() => {
    const total = breakdown.reduce((sum, item) => sum + parseFloat(item.percentage || 0), 0);
    setTotalPercentage(total);
    updateFormData({ funding_use: breakdown });
  }, [breakdown, updateFormData]);

  const addBreakdownItem = () => {
    if (breakdown.length < 10) {
      const newItem = {
        category: '',
        amount: '',
        percentage: '',
        description: ''
      };
      setBreakdown([...breakdown, newItem]);
    }
  };

  const removeBreakdownItem = (index) => {
    const newBreakdown = breakdown.filter((_, i) => i !== index);
    setBreakdown(newBreakdown);
  };

  const updateBreakdownItem = (index, field, value) => {
    const newBreakdown = [...breakdown];
    newBreakdown[index] = { ...newBreakdown[index], [field]: value };

    // Auto-calculate amount or percentage based on funding goal
    const fundingGoal = parseFloat(formData.funding_goal || 0);
    
    if (field === 'percentage' && fundingGoal > 0) {
      const percentage = parseFloat(value || 0);
      const amount = (fundingGoal * percentage / 100).toFixed(2);
      newBreakdown[index].amount = amount;
    } else if (field === 'amount' && fundingGoal > 0) {
      const amount = parseFloat(value || 0);
      const percentage = ((amount / fundingGoal) * 100).toFixed(2);
      newBreakdown[index].percentage = percentage;
    }

    setBreakdown(newBreakdown);
  };

  const useSuggestedCategory = (suggestion) => {
    if (breakdown.length < 10) {
      const newItem = {
        category: suggestion.category,
        amount: '',
        percentage: '',
        description: suggestion.description
      };
      setBreakdown([...breakdown, newItem]);
    }
  };

  const validateBreakdown = () => {
    const newErrors = { ...errors };

    if (breakdown.length === 0) {
      newErrors.funding_use = 'At least one funding category is required';
    } else {
      // Check if all items have required fields
      const hasIncompleteItems = breakdown.some(item => 
        !item.category || !item.description || !item.amount || !item.percentage
      );

      if (hasIncompleteItems) {
        newErrors.funding_use = 'All funding categories must be complete';
      } else if (Math.abs(totalPercentage - 100) > 0.01) {
        newErrors.funding_use = 'Percentages must sum to 100%';
      } else {
        delete newErrors.funding_use;
      }
    }

    setErrors(newErrors);
  };

  // Validate whenever breakdown changes
  useEffect(() => {
    if (breakdown.length > 0) {
      validateBreakdown();
    }
  }, [breakdown, totalPercentage]);

  const getPercentageColor = () => {
    if (Math.abs(totalPercentage - 100) < 0.01) return 'text-green-600';
    if (totalPercentage > 100) return 'text-red-600';
    return 'text-orange-600';
  };

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Header */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            💰 Funding Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <p className="text-sm sm:text-p2-r text-neutral-600">
            Show donors exactly how their contributions will be used. Be transparent and specific about your funding allocation.
          </p>
        </CardContent>
      </Card>

      {/* Total Summary */}
      {breakdown.length > 0 && (
        <Card className="border-0 shadow-soft bg-gradient-to-br from-primary-teal/5 to-blue-50/50">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm">
                  <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                </div>
                <div>
                  <p className="text-sm sm:text-p1-m font-semibold text-neutral-900">Total Allocation</p>
                  <p className="text-xs sm:text-p2-r text-neutral-500">
                    {breakdown.length} {breakdown.length === 1 ? 'category' : 'categories'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-xl sm:text-h4 font-bold tracking-tight ${getPercentageColor()}`}>
                  {new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(totalPercentage)}%
                </p>
                <p className="text-xs sm:text-p2-r font-medium text-neutral-600">
                  {getCurrencySymbol(formData.currency)} {breakdown.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
            </div>
            
            {Math.abs(totalPercentage - 100) > 0.01 && (
              <div className="mt-3 p-2.5 bg-white/60 rounded-xl border border-white/40">
                <p className={`text-xs sm:text-p2-r font-medium text-center ${getPercentageColor()}`}>
                  {totalPercentage > 100 
                    ? `⚠️ Over by ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(totalPercentage - 100)}%` 
                    : `ℹ️ Remaining: ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(100 - totalPercentage)}%`
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Breakdown Items */}
      <AnimatePresence>
        {breakdown.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3 border-b border-neutral-100/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-p1-m font-heading text-neutral-900">
                    Category {index + 1}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBreakdownItem(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
                  >
                    <Trash2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Remove</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4">
                {/* Category Name */}
                <div>
                  <label className="block text-sm sm:text-p2-m font-medium text-neutral-900 mb-2">
                    Category Name *
                  </label>
                  <Input
                    type="text"
                    value={item.category}
                    onChange={(e) => updateBreakdownItem(index, 'category', e.target.value)}
                    placeholder="e.g., Development, Marketing, Equipment"
                    maxLength={50}
                    className="h-10 sm:h-11"
                  />
                </div>

                {/* Amount and Percentage */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm sm:text-p2-m font-medium text-neutral-900 mb-2">
                      Amount ({getCurrencySymbol(formData.currency)}) *
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-bold text-sm">
                        {getCurrencySymbol(formData.currency)}
                      </div>
                      <Input
                        type="number"
                        value={item.amount}
                        onChange={(e) => updateBreakdownItem(index, 'amount', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="pl-8 h-10 sm:h-11"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm sm:text-p2-m font-medium text-neutral-900 mb-2">
                      Share (%) *
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={item.percentage}
                        onChange={(e) => updateBreakdownItem(index, 'percentage', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        max="100"
                        step="0.01"
                        className="pr-8 h-10 sm:h-11"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 font-medium text-sm">
                        %
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm sm:text-p2-m font-medium text-neutral-900 mb-2">
                    What's this for? *
                  </label>
                  <textarea
                    value={item.description}
                    onChange={(e) => updateBreakdownItem(index, 'description', e.target.value)}
                    placeholder="Explain how this portion of the funds will be used..."
                    rows={3}
                    maxLength={200}
                    className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-xl bg-neutral-50/30 focus:ring-2 focus:ring-primary-teal focus:border-transparent resize-none transition-all duration-200"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-[10px] sm:text-xs text-neutral-400">
                      {item.description.length}/200
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add Category Button */}
      {breakdown.length < 10 && (
        <Card className="border-2 border-dashed border-neutral-200 hover:border-primary-teal/50 transition-all duration-300 bg-neutral-50/30">
          <CardContent className="p-6 sm:p-8 text-center">
            <Button
              variant="outline"
              onClick={addBreakdownItem}
              className="flex items-center gap-2 mx-auto h-11 px-6 rounded-xl border-neutral-300 hover:border-primary-teal text-neutral-700 hover:text-primary-teal"
            >
              <Plus className="w-5 h-5" />
              Add Funding Category
            </Button>
            <p className="text-xs sm:text-p2-r text-neutral-500 mt-3 font-medium">
              Include up to {10 - breakdown.length} more {breakdown.length === 9 ? 'category' : 'categories'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Suggested Categories */}
      {breakdown.length === 0 && (
        <Card className="border-0 shadow-soft">
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="text-base sm:text-p1-m font-heading text-neutral-900">
              Suggested Categories
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {FUNDING_BREAKDOWN_CATEGORIES.map((suggestion, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => useSuggestedCategory(suggestion)}
                  className="group flex items-start gap-3 p-3.5 border border-neutral-200 rounded-xl hover:border-primary-teal hover:bg-primary-teal/5 transition-all duration-200 text-left active:bg-primary-teal/10"
                >
                  <div className="p-1 rounded-full bg-neutral-100 group-hover:bg-primary-teal/10 transition-colors">
                    <Plus className="w-3.5 h-3.5 text-neutral-500 group-hover:text-primary-teal" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-sm font-semibold text-neutral-900 truncate">{suggestion.category}</p>
                    <p className="text-[11px] sm:text-p2-r text-neutral-500 leading-tight mt-0.5 line-clamp-2">{suggestion.description}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation Errors */}
      {errors.funding_use && (
        <Card className="bg-red-50 border-red-200 border-2">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-p2-m text-red-800 mb-1">Funding Breakdown Error</p>
                <p className="text-p2-r text-red-700">{errors.funding_use}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="bg-blue-50 border-blue-200 border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <PieChart className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-p2-r text-blue-800">
              <p className="font-semibold mb-1">Breakdown Tips</p>
              <ul className="space-y-1 text-sm">
                <li>• Be specific about how each portion will be used</li>
                <li>• Percentages must add up to exactly 100%</li>
                <li>• Include major expense categories</li>
                <li>• Transparency builds donor trust</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FundingBreakdownStep;
